import { useState, useRef, useEffect, useCallback } from 'react';

// Animation duration in milliseconds (must match CSS transition duration)
const MODAL_ANIMATION_DURATION = 150;
const MODAL_MOUNT_DELAY = 10; // Small delay to allow DOM to render before animating

/**
 * Custom hook for managing modal animation state
 *
 * Handles the timing for fade-in/scale animations when opening/closing modals.
 * Prevents memory leaks by cleaning up timeouts on unmount.
 *
 * @param isOpen - Whether the modal is currently open
 * @returns Object containing isAnimated state and handlers
 *
 * @example
 * ```tsx
 * const [showModal, setShowModal] = useState(false);
 * const { isAnimated, open, close } = useModalAnimation(showModal);
 *
 * const handleOpen = () => {
 *   setShowModal(true);
 *   open();
 * };
 *
 * const handleClose = () => {
 *   close(() => setShowModal(false));
 * };
 * ```
 */
export function useModalAnimation(isOpen: boolean) {
  const [isAnimated, setIsAnimated] = useState(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount or when isOpen changes
  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  // Trigger animation when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      // Start animation after a brief delay (allows DOM to render)
      openTimeoutRef.current = setTimeout(() => {
        setIsAnimated(true);
        openTimeoutRef.current = null;
      }, MODAL_MOUNT_DELAY);
    } else {
      // Modal is closed, schedule setIsAnimated(false) asynchronously to avoid setState in effect
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        setIsAnimated(false);
        closeTimeoutRef.current = null;
      }, 0);
    }
  }, [isOpen]);

  /**
   * Manually trigger the open animation
   * Call this after setting isOpen to true
   */
  const open = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    openTimeoutRef.current = setTimeout(() => {
      setIsAnimated(true);
      openTimeoutRef.current = null;
    }, MODAL_MOUNT_DELAY);
  }, []);

  /**
   * Trigger the close animation, then call the callback
   * @param onComplete - Callback to run after animation completes (e.g., setIsOpen(false))
   */
  const close = useCallback((onComplete?: () => void) => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    setIsAnimated(false);
    closeTimeoutRef.current = setTimeout(() => {
      onComplete?.();
      closeTimeoutRef.current = null;
    }, MODAL_ANIMATION_DURATION);
  }, []);

  return {
    isAnimated,
    open,
    close,
  };
}
